import { RefObject, useCallback, useEffect, useRef } from 'react';

import {
  isTranscribingLabel,
  TRANSCRIBING_BASE,
  TRANSCRIBING_DOT_FRAMES,
  useSpeechToText,
  SpeechToTextStatus,
} from './useSpeechToText';

type UseChatSpeechInputArgs = {
  messageRef: RefObject<HTMLTextAreaElement | null>;
  setInputMessage: (value: string | null) => void;
  setShowSendButton: (show: boolean) => void;
  language?: string;
};

/**
 * Binds Whisper STT to a chat textarea: animated Transcribing label, draft restore, transcript fill.
 */
export const useChatSpeechInput = ({
  messageRef,
  setInputMessage,
  setShowSendButton,
  language = 'en',
}: UseChatSpeechInputArgs) => {
  const draftBeforeTranscribeRef = useRef('');

  const applyTranscriptToInput = useCallback(
    (text: string) => {
      const textarea = messageRef.current;
      if (!textarea) return;
      const existing = draftBeforeTranscribeRef.current.trim();
      const next = existing ? `${existing} ${text}` : text;
      textarea.readOnly = false;
      textarea.value = next;
      textarea.style.height = '56px';
      textarea.style.height = `${textarea.scrollHeight}px`;
      setInputMessage(next);
      setShowSendButton(next.trim().length > 0);
      textarea.focus();
    },
    [messageRef, setInputMessage, setShowSendButton],
  );

  const { status: micStatus, toggleRecording } = useSpeechToText({
    language,
    onTranscript: applyTranscriptToInput,
  });

  useEffect(() => {
    const textarea = messageRef.current;
    if (!textarea) return;

    if (micStatus === 'transcribing') {
      if (!isTranscribingLabel(textarea.value)) {
        draftBeforeTranscribeRef.current = textarea.value;
      }
      let frame = 0;
      textarea.value = `${TRANSCRIBING_BASE}${TRANSCRIBING_DOT_FRAMES[frame]}`;
      textarea.readOnly = true;
      setShowSendButton(false);

      const timer = window.setInterval(() => {
        frame = (frame + 1) % TRANSCRIBING_DOT_FRAMES.length;
        if (messageRef.current) {
          messageRef.current.value = `${TRANSCRIBING_BASE}${TRANSCRIBING_DOT_FRAMES[frame]}`;
        }
      }, 350);

      return () => window.clearInterval(timer);
    }

    textarea.readOnly = false;
    if (isTranscribingLabel(textarea.value)) {
      const restored = draftBeforeTranscribeRef.current;
      textarea.value = restored;
      setInputMessage(restored.trim() || null);
      setShowSendButton(restored.trim().length > 0);
    }
  }, [micStatus, messageRef, setInputMessage, setShowSendButton]);

  const isTranscribing = micStatus === 'transcribing';
  const isSpeechBusy = isTranscribing || micStatus === 'recording';

  /** True when the textarea value must not be submitted (placeholder / mid-STT). */
  const shouldBlockSend = useCallback(
    (value?: string) => {
      if (isTranscribing) return true;
      const text = value ?? messageRef.current?.value ?? '';
      return isTranscribingLabel(text.trim());
    },
    [isTranscribing, messageRef],
  );

  return {
    micStatus: micStatus as SpeechToTextStatus,
    toggleRecording,
    isTranscribing,
    isSpeechBusy,
    shouldBlockSend,
  };
};
